import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { NotificationResponse, UserAlert } from './notification.model';

@Injectable()
export class NotificationApiHandlerService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches notifications with pagination.
   * @param pageSize - The number of notifications per page.
   * @param pageNumber - The current page number.
   * @returns An observable for the HTTP response containing notifications.
   */
  getNotifications(payload: {
    pageSize: number;
    pageNumber: number;
    searchQuery?: string;
  }) {
    return this.http.post<NotificationResponse>(
      `${environment.genUrl}api/notification/all`,
      payload
    );
  }

  /**
   * Fetches the top unread notifications.
   * @param limit - The maximum number of unread notifications to fetch.
   * @returns An observable for the HTTP response containing unread notifications.
   */
  getTopUnreadNotifications(limit: number = 10) {
    return this.http.get<NotificationResponse>(
      `${environment.genUrl}api/notification/all/unread?limit=${limit}`
    );
  }

  /**
   * Fetches user alert settings.
   * @returns An observable for the HTTP response containing user alert settings.
   */
  getUserAlertSettings() {
    return this.http.get<UserAlert>(
      `${environment.genUrl}api/app_settings/user-settings`
    );
  }

  /**
   * Updates user alert settings.
   * @param payload - The user alert settings to update.
   * @returns An observable for the HTTP response indicating the update status.
   */
  updateUserAlertSettings(payload: UserAlert) {
    return this.http.post<UserAlert>(
      `${environment.genUrl}api/app_settings/user-settings`,
      payload
    );
  }

  /**
   * Marks a specific notification as read.
   * @param notificationId - The ID of the notification to mark as read.
   * @returns An observable for the HTTP response indicating the notification has been marked as read.
   */
  markNotificationsAsRead(notificationIds?: string[]) {
    return this.http.put(
      `${environment.genUrl}api/notification/read`,
      notificationIds
    );
  }

  /**
   * Deletes a specific notification.
   * @param notificationId - The ID of the notification to delete.
   * @returns An observable for the HTTP response indicating the notification has been deleted.
   */
  deleteNotifications(notificationIds?: string[]) {
    return this.http.delete(`${environment.genUrl}api/notification`, {
      body: notificationIds,
    });
  }
}
